/**
 * @file These functions attempt to coerce input values into the desired output value.
 * Their output is not strongly typed, as it is meant to be passed to a validator. These
 * are useful, for example, when parsing query strings, which are always string values.
 */

import * as z from 'zod';

/**
 * Require an array containing exactly one valid entry, then return that entry.
 */
export const zodSingleFromArray = <T extends z.ZodType>(schema: T) =>
    z
        .array(schema)
        .length(1, 'Expected exactly one value')
        .transform(([value]) => value);

/**
 * Accept values that can be coerced into a `boolean`.
 */
export const zodBooleanLike = (params?: z.core.$ZodUnionParams) =>
    z
        .union(
            [
                z.bigint(),
                z.number(),
                z.enum(['true', '1', 'false', '0']),
                z.boolean(),
            ],
            params
        )
        .transform((val): boolean => {
            switch (typeof val) {
                case 'bigint':
                case 'number':
                    return val > 0;

                case 'string':
                    if (['true', '1'].includes(val)) {
                        return true;
                    } else {
                        return false;
                    }

                case 'boolean':
                    return val;

                default:
                    val satisfies never;
                    return val;
            }
        });

/**
 * Accept values that can be coerced into a `number`.
 */
export const zodNumberLike = (params?: z.core.$ZodUnionParams) =>
    z
        .union([z.number(), z.string().min(1)], params)
        .transform((val): number => {
            switch (typeof val) {
                case 'number':
                    return Number(val);

                case 'string':
                    return Number(val);

                default:
                    val satisfies never;
                    return val;
            }
        });

export const zodDateLike = (params?: z.core.$ZodUnionParams) =>
    z
        .union([z.date(), z.string().min(1)], params)
        .transform((val): Date => {
            switch (typeof val) {
                case 'string':
                    return new Date(val);

                default:
                    return val;
            }
        })
        .pipe(
            // This does result in double-validation for actual Date inputs, but
            // it also allows Zod to do final checks on what was initially a string
            // (ensuring Date is actually valid, etc).
            z.date()
        );

export const zodStringLike = (params?: z.core.$ZodUnionParams) =>
    z
        .union([z.string(), z.number(), z.bigint(), z.boolean()], params)
        .transform((val): string => {
            switch (typeof val) {
                case 'bigint':
                case 'boolean':
                case 'number':
                    return val.toString();

                case 'string':
                    return val;

                default:
                    val satisfies never;
                    return val;
            }
        });

/**
 * Supports arrays or string arrays that may be represented
 * by a comma-separated string (ie. `a,b,c`).
 */
export const zodArrayOrStringArray = (params?: z.core.$ZodUnionParams) =>
    z
        .union([z.array(z.string()), z.string()], params)
        .transform((val): string[] => {
            if (Array.isArray(val)) {
                return val;
            }

            if (val.length === 0) {
                return [];
            }

            return val.split(',').filter((e) => e);
        });

/**
 * Validate a valid base64-encoded value and then decode it
 */
export const zodStringFromBase64 = (params?: z.core.$ZodBase64Params) =>
    z.base64(params).transform((val) => {
        return Buffer.from(val, 'base64').toString('utf-8');
    });
